import React from "react";
import { ArrowRight } from "lucide-react";
import type { CategoryDetails, SubCategory } from "../../../types";
import { servicesImageCardsTokens } from "./ServicesImageCards.tokens";

export type ServicesImageCardsLinkComponent = React.ComponentType<{
  href: string;
  children?: React.ReactNode;
  className?: string;
  title?: string;
  "aria-label"?: string;
  [key: string]: any;
}>;

export type ServicesImageCardsImage = {
  src?: string;
  url?: string;
  original?: string;
  thumbnail?: string;
  alt?: string;
  title?: string;
  originalAlt?: string;
  thumbnailAlt?: string;
};

export type ServicesImageCardsTileCta = {
  label?: string;
  href?: string;
  ariaLabel?: string;
};

type ServicesImageCardsSubCategoryFields = Omit<
  Partial<SubCategory>,
  | "id"
  | "title"
  | "name"
  | "description"
  | "link"
  | "images"
  | "heroImage"
  | "accentColor"
  | "accentDarkColor"
>;

export type ServicesImageCardsTile = ServicesImageCardsSubCategoryFields & {
  id: string | number;
  title?: string;
  name?: string;
  text?: string;
  description?: string;
  href?: string;
  link?: string;
  image?: ServicesImageCardsImage;
  images?: ServicesImageCardsImage[];
  heroImage?: ServicesImageCardsImage;
  imageUrl?: string;
  imageAlt?: string;
  icon?: React.ReactNode;
  iconSrc?: string;
  iconAlt?: string;
  accentColor?: string;
  accentDarkColor?: string;
  cta?: ServicesImageCardsTileCta;
  ctaLabel?: string;
  ctaHref?: string;
  ctaAriaLabel?: string;
};

export type ServicesImageCardsProps = {
  items?: ServicesImageCardsTile[];
  service?: CategoryDetails;
  Link: ServicesImageCardsLinkComponent;
  overline?: string;
  title?: string;
  text?: string;
  description?: string;
  tileCtaLabel?: string;
  accentColor?: string;
  backgroundColor?: string;
  maxWidth?: number | string;
  cardWidth?: number | string;
  imageHeight?: number | string;
  className?: string;
  ariaLabel?: string;
};

const t = servicesImageCardsTokens;

const servicesImageCardsCss = `
.services-image-cards {
  --services-image-cards-bg: ${t.colors.background};
  --services-image-cards-accent: ${t.colors.accent};
  --services-image-cards-card-width: ${t.sizes.cardWidth}px;
  --services-image-cards-image-height: ${t.sizes.imageHeight}px;
  width: 100%;
  background: var(--services-image-cards-bg);
  color: ${t.colors.text};
  font-family: ${t.typography.fontFamily};
  box-sizing: border-box;
}
.services-image-cards,
.services-image-cards * {
  box-sizing: border-box;
}
.services-image-cards-shell {
  width: 100%;
  max-width: ${t.sizes.maxWidth}px;
  margin: 0 auto;
  padding: ${t.spacing.shellY}px ${t.spacing.shellX}px;
}
.services-image-cards-header {
  display: grid;
  grid-template-columns: minmax(0, 0.94fr) minmax(280px, 0.74fr);
  align-items: start;
  gap: 42px;
  margin-bottom: 34px;
}
.services-image-cards-kicker {
  margin: 0 0 15px;
  color: var(--services-image-cards-accent);
  font-size: ${t.typography.overline.fontSize}px;
  line-height: ${t.typography.overline.lineHeight};
  font-weight: ${t.typography.overline.fontWeight};
  letter-spacing: ${t.typography.overline.letterSpacing};
  text-transform: ${t.typography.overline.textTransform};
}
.services-image-cards-title {
  max-width: 520px;
  margin: 0;
  color: ${t.colors.text};
  font-size: ${t.typography.title.fontSize}px;
  line-height: ${t.typography.title.lineHeight};
  font-weight: ${t.typography.title.fontWeight};
  letter-spacing: ${t.typography.title.letterSpacing};
}
.services-image-cards-text {
  max-width: 460px;
  margin: 31px 0 0;
  color: ${t.colors.textSoft};
  font-size: ${t.typography.text.fontSize}px;
  line-height: ${t.typography.text.lineHeight};
  font-weight: ${t.typography.text.fontWeight};
  letter-spacing: ${t.typography.text.letterSpacing};
}
.services-image-cards-viewport {
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  padding: 2px 0 14px;
  scroll-behavior: smooth;
  scroll-snap-type: x proximity;
  scrollbar-width: thin;
  scrollbar-color: ${t.colors.scrollbarThumb} ${t.colors.scrollbarTrack};
  -webkit-overflow-scrolling: touch;
}
.services-image-cards-viewport:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--services-image-cards-accent) 22%, transparent);
  outline-offset: 6px;
  border-radius: ${t.radius.card}px;
}
.services-image-cards-viewport::-webkit-scrollbar {
  height: 8px;
}
.services-image-cards-viewport::-webkit-scrollbar-track {
  border-radius: 999px;
  background: ${t.colors.scrollbarTrack};
}
.services-image-cards-viewport::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: ${t.colors.scrollbarThumb};
}
.services-image-cards-track {
  display: flex;
  align-items: stretch;
  justify-content: flex-start;
  gap: ${t.spacing.cardGap}px;
  min-width: max-content;
}
.services-image-cards-item {
  width: var(--services-image-cards-card-width);
  flex: 0 0 var(--services-image-cards-card-width);
  scroll-snap-align: start;
  display: flex;
}
.services-image-cards-link {
  width: 100%;
  min-width: 0;
  display: flex;
  color: inherit;
  text-decoration: none;
}
.services-image-cards-card {
  position: relative;
  display: grid;
  grid-template-rows: var(--services-image-cards-image-height) minmax(0, 1fr);
  width: 100%;
  min-height: 398px;
  overflow: hidden;
  border: 1px solid ${t.colors.border};
  border-radius: ${t.radius.card}px;
  background: ${t.colors.card};
  box-shadow: ${t.colors.shadow};
  transition: ${t.motion.card};
}
.services-image-cards-link:hover .services-image-cards-card,
.services-image-cards-link:focus-visible .services-image-cards-card {
  transform: translateY(-2px);
  border-color: color-mix(in srgb, var(--tile-accent, var(--services-image-cards-accent)) 28%, ${t.colors.border});
  box-shadow: ${t.colors.hoverShadow};
}
.services-image-cards-link:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--tile-accent, var(--services-image-cards-accent)) 26%, transparent);
  outline-offset: 4px;
  border-radius: ${t.radius.card}px;
}
.services-image-cards-image-wrap {
  position: relative;
  width: 100%;
  min-width: 0;
  height: var(--services-image-cards-image-height);
}
.services-image-cards-image-frame {
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: ${t.colors.imageFallback};
}
.services-image-cards-image,
.services-image-cards-image-fallback {
  display: block;
  width: 100%;
  height: 100%;
}
.services-image-cards-image {
  object-fit: cover;
  transform: scale(1);
  transition: ${t.motion.image};
}
.services-image-cards-link:hover .services-image-cards-image,
.services-image-cards-link:focus-visible .services-image-cards-image {
  transform: scale(1.075);
}
.services-image-cards-image-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  color: ${t.colors.muted};
  font-size: 13px;
  line-height: 1.4;
  font-weight: 700;
  text-align: center;
  background: linear-gradient(180deg, #f8fbff 0%, #edf2f7 100%);
}
.services-image-cards-icon-badge {
  position: absolute;
  z-index: 2;
  bottom: calc(${t.sizes.iconSize}px / -2);
  left: 24px;
  width: ${t.sizes.iconSize}px;
  height: ${t.sizes.iconSize}px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${t.colors.badgeBorder};
  border-radius: ${t.radius.badge}px;
  background: ${t.colors.card};
  color: var(--tile-accent, var(--services-image-cards-accent));
  box-shadow: 0 10px 22px rgba(7, 94, 214, 0.13);
  pointer-events: none;
}
.services-image-cards-icon-node,
.services-image-cards-icon-node svg,
.services-image-cards-icon-image {
  width: 26px;
  height: 26px;
  display: block;
}
.services-image-cards-icon-node svg {
  stroke-width: 2.1;
}
.services-image-cards-icon-image {
  object-fit: contain;
}
.services-image-cards-content {
  display: flex;
  min-height: 0;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  padding: 45px ${t.spacing.contentX}px ${t.spacing.contentBottom}px;
}
.services-image-cards-card[data-has-icon="false"] .services-image-cards-content {
  padding-top: 24px;
}
.services-image-cards-tile-title {
  max-width: 100%;
  margin: 0;
  color: ${t.colors.text};
  font-size: ${t.typography.tileTitle.fontSize}px;
  line-height: ${t.typography.tileTitle.lineHeight};
  font-weight: ${t.typography.tileTitle.fontWeight};
  letter-spacing: ${t.typography.tileTitle.letterSpacing};
}
.services-image-cards-tile-text {
  margin: 0;
  color: ${t.colors.textSoft};
  font-size: ${t.typography.tileText.fontSize}px;
  line-height: ${t.typography.tileText.lineHeight};
  font-weight: ${t.typography.tileText.fontWeight};
  letter-spacing: ${t.typography.tileText.letterSpacing};
}
.services-image-cards-tile-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: auto;
  padding-top: 4px;
  color: var(--tile-accent, var(--services-image-cards-accent));
  font-size: ${t.typography.cta.fontSize}px;
  line-height: ${t.typography.cta.lineHeight};
  font-weight: ${t.typography.cta.fontWeight};
  letter-spacing: ${t.typography.cta.letterSpacing};
  text-transform: ${t.typography.cta.textTransform};
  white-space: nowrap;
}
.services-image-cards-tile-cta svg {
  flex: 0 0 auto;
  transition: ${t.motion.cta};
}
.services-image-cards-link:hover .services-image-cards-tile-cta svg,
.services-image-cards-link:focus-visible .services-image-cards-tile-cta svg {
  transform: translateX(3px);
}
@media (max-width: 767px) {
  .services-image-cards-shell {
    padding: ${t.spacing.mobileShellY}px ${t.spacing.mobileShellX}px;
  }
  .services-image-cards-header {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 15px;
    margin-bottom: 24px;
  }
  .services-image-cards-kicker {
    margin-bottom: 14px;
    font-size: 11px;
  }
  .services-image-cards-title {
    max-width: 430px;
    font-size: 28px;
    line-height: 1.16;
  }
  .services-image-cards-text {
    max-width: 430px;
    margin: 0;
    font-size: 14px;
    line-height: 1.55;
  }
  .services-image-cards-viewport {
    overflow: visible;
    padding: 0;
    scroll-snap-type: none;
  }
  .services-image-cards-track {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 240px), 1fr));
    min-width: 0;
    gap: ${t.spacing.mobileCardGap}px;
  }
  .services-image-cards-item {
    width: 100%;
    min-width: 0;
    flex-basis: auto;
  }
  .services-image-cards-card {
    min-height: 0;
    grid-template-rows: auto minmax(0, 1fr);
  }
  .services-image-cards-image-wrap {
    height: auto;
    aspect-ratio: 16 / 9;
  }
  .services-image-cards-icon-badge {
    bottom: calc(${t.sizes.mobileIconSize}px / -2);
    left: 12px;
    width: ${t.sizes.mobileIconSize}px;
    height: ${t.sizes.mobileIconSize}px;
  }
  .services-image-cards-icon-node,
  .services-image-cards-icon-node svg,
  .services-image-cards-icon-image {
    width: 21px;
    height: 21px;
  }
  .services-image-cards-content {
    gap: 11px;
    padding: 34px 12px 16px;
  }
  .services-image-cards-card[data-has-icon="false"] .services-image-cards-content {
    padding-top: 16px;
  }
  .services-image-cards-tile-title {
    font-size: 14px;
    line-height: 1.22;
  }
  .services-image-cards-tile-text {
    font-size: 12px;
    line-height: 1.48;
  }
  .services-image-cards-tile-cta {
    gap: 6px;
    font-size: 11px;
  }
}
@media (max-width: 380px) {
  .services-image-cards-shell {
    padding-left: 12px;
    padding-right: 12px;
  }
  .services-image-cards-track {
    gap: 12px;
  }
  .services-image-cards-card {
    min-height: 0;
  }
  .services-image-cards-content {
    padding-left: 10px;
    padding-right: 10px;
  }
}
`;

function getImage(item: ServicesImageCardsTile): ServicesImageCardsImage {
  if (item.image) return item.image;

  const heroImage = item.heroImage as ServicesImageCardsImage | undefined;
  if (heroImage) return heroImage;

  const galleryImage = item.images?.[0] as ServicesImageCardsImage | undefined;
  if (galleryImage) return galleryImage;

  if (item.imageUrl) {
    return {
      src: item.imageUrl,
      alt: item.imageAlt || item.title || item.name,
    };
  }

  return {};
}

function getImageSrc(image: ServicesImageCardsImage): string {
  return image.thumbnail || image.src || image.url || image.original || "";
}

function getImageAlt(image: ServicesImageCardsImage, title: string): string {
  return image.alt || image.thumbnailAlt || image.originalAlt || image.title || title;
}

function getItemTitle(item: ServicesImageCardsTile): string {
  return item.title || item.name || "Service";
}

function getItemText(item: ServicesImageCardsTile): string | undefined {
  return item.text || item.description;
}

function getItemHref(item: ServicesImageCardsTile): string {
  return item.cta?.href || item.ctaHref || item.href || item.link || "#";
}

function getItemCtaLabel(item: ServicesImageCardsTile, fallback: string): string {
  return item.cta?.label || item.ctaLabel || fallback;
}

function getSectionText(service?: CategoryDetails, description?: string, text?: string) {
  if (text) return text;
  if (description) return description;
  if (Array.isArray(service?.description)) return service.description.join(" ");
  return undefined;
}

function getCssSize(value: number | string): string {
  return typeof value === "number" ? `${value}px` : value;
}

function ServicesImageCardsIcon({ item }: { item: ServicesImageCardsTile }) {
  if (item.iconSrc) {
    return <img src={item.iconSrc} alt={item.iconAlt || ""} className="services-image-cards-icon-image" />;
  }

  if (item.icon) {
    return <span className="services-image-cards-icon-node">{item.icon}</span>;
  }

  return null;
}

export default function ServicesImageCards({
  items,
  service,
  Link,
  overline = "Our Services",
  title,
  text,
  description,
  tileCtaLabel = "Learn More",
  accentColor = t.colors.accent,
  backgroundColor = t.colors.background,
  maxWidth = t.sizes.maxWidth,
  cardWidth = t.sizes.cardWidth,
  imageHeight = t.sizes.imageHeight,
  className = "",
  ariaLabel,
}: ServicesImageCardsProps) {
  const resolvedItems = (items?.length ? items : service?.subCategories || []) as ServicesImageCardsTile[];
  const resolvedTitle = title || service?.long_name || service?.name;
  const resolvedText = getSectionText(service, description, text);

  if (!resolvedItems.length) return null;

  return (
    <section
      className={`services-image-cards ${className}`}
      aria-label={ariaLabel || resolvedTitle || overline}
      style={
        {
          "--services-image-cards-bg": backgroundColor,
          "--services-image-cards-accent": accentColor,
          "--services-image-cards-card-width": getCssSize(cardWidth),
          "--services-image-cards-image-height": getCssSize(imageHeight),
        } as React.CSSProperties
      }
    >
      <style>{servicesImageCardsCss}</style>

      <div className="services-image-cards-shell" style={{ maxWidth: getCssSize(maxWidth) }}>
        {overline || resolvedTitle || resolvedText ? (
          <div className="services-image-cards-header">
            <div>
              {overline ? <p className="services-image-cards-kicker">{overline}</p> : null}
              {resolvedTitle ? <h2 className="services-image-cards-title">{resolvedTitle}</h2> : null}
            </div>

            {resolvedText ? <p className="services-image-cards-text">{resolvedText}</p> : null}
          </div>
        ) : null}

        <div className="services-image-cards-viewport" aria-label="Services" tabIndex={0}>
          <div className="services-image-cards-track">
            {resolvedItems.map((item) => {
              const itemTitle = getItemTitle(item);
              const itemText = getItemText(item);
              const image = getImage(item);
              const imageSrc = getImageSrc(image);
              const href = getItemHref(item);
              const ctaLabel = getItemCtaLabel(item, tileCtaLabel);
              const itemAccent = item.accentColor || item.accentDarkColor || accentColor;
              const hasIcon = Boolean(item.icon || item.iconSrc);

              return (
                <div
                  key={item.id}
                  className="services-image-cards-item"
                  style={{ "--tile-accent": itemAccent } as React.CSSProperties}
                >
                  <Link
                    href={href}
                    title={itemTitle}
                    aria-label={item.cta?.ariaLabel || item.ctaAriaLabel || `${ctaLabel}: ${itemTitle}`}
                    className="services-image-cards-link"
                  >
                    <article className="services-image-cards-card" data-has-icon={hasIcon ? "true" : "false"}>
                      <div className="services-image-cards-image-wrap">
                        <div className="services-image-cards-image-frame">
                          {imageSrc ? (
                            <img
                              src={imageSrc}
                              alt={getImageAlt(image, itemTitle)}
                              className="services-image-cards-image"
                              loading="lazy"
                            />
                          ) : (
                            <div className="services-image-cards-image-fallback">{itemTitle}</div>
                          )}
                        </div>

                        {hasIcon ? (
                          <span className="services-image-cards-icon-badge" aria-hidden="true">
                            <ServicesImageCardsIcon item={item} />
                          </span>
                        ) : null}
                      </div>

                      <div className="services-image-cards-content">
                        <h3 className="services-image-cards-tile-title">{itemTitle}</h3>
                        {itemText ? <p className="services-image-cards-tile-text">{itemText}</p> : null}
                        <span className="services-image-cards-tile-cta">
                          {ctaLabel}
                          <ArrowRight size={14} strokeWidth={2.4} aria-hidden="true" />
                        </span>
                      </div>
                    </article>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
